import {
    AllSubscriptionsByType,
    AllSubscriptionTypes,
    CompanySubscription,
    CreditSubscription,
    DesignerSubscription,
    Subscription,
    SubscriptionBillingCycle,
    SubscriptionData,
    SubscriptionFromSubscriptionType,
    SubscriptionType,
    SubscriptionTypeLabelMap,
    Team,
} from 'models';

type GenericSubscriptionData<T extends SubscriptionType> = {
    subscription: SubscriptionFromSubscriptionType<T>;
    quantity: number;
};

type ChangesForType<T extends SubscriptionType> = NonNullable<Team['subscriptionUpdateNotice']>['changes'][number] & {
    subscription: SubscriptionFromSubscriptionType<T>;
};

export const SubscriptionHelpers = {
    isFreeSubscription: (
        subscription?: Subscription,
    ): subscription is CompanySubscription.NONE | CreditSubscription.NONE | DesignerSubscription.NONE =>
        subscription === CompanySubscription.NONE ||
        subscription === CreditSubscription.NONE ||
        subscription === DesignerSubscription.NONE,

    assertNewCompanyPremiumIsAnnual: ({
        subscriptions,
        billingCycle,
    }: {
        subscriptions: { subscription: Subscription; quantity: number }[];
        billingCycle: SubscriptionBillingCycle;
    }) => {
        if (!SubscriptionHelpers.includesPaidCompanyPremium(subscriptions)) {
            return;
        }

        if (billingCycle !== SubscriptionBillingCycle.YEARLY) {
            throw new Error('New Company Premium subscriptions must be annual');
        }
    },

    includesPaidCompanyPremium: (subscriptions: { subscription: Subscription; quantity: number }[]): boolean =>
        subscriptions.some(
            ({ subscription, quantity }) => subscription === CompanySubscription.PREMIUM && quantity > 0,
        ),

    isSubscriptionType: <T extends SubscriptionType>(
        type: T,
        subscription?: Subscription | null,
    ): subscription is SubscriptionFromSubscriptionType<T> =>
        subscription !== undefined && subscription !== null && AllSubscriptionsByType[type].includes(subscription),

    getSubscriptionAndType: <T extends SubscriptionType = SubscriptionType>(
        subscription: SubscriptionFromSubscriptionType<T>,
    ): { type: T; subscription: SubscriptionFromSubscriptionType<T> } => {
        const type = AllSubscriptionTypes.find((type) => SubscriptionHelpers.isSubscriptionType(type, subscription)) as
            | T
            | undefined;

        if (!type) {
            throw new Error(`Subscription ${subscription} does not match any known subscription type`);
        }

        return { type, subscription };
    },

    getSubscriptionData: <T extends SubscriptionType>(
        type: T,
        subscriptions: SubscriptionData[] | undefined,
    ): GenericSubscriptionData<T> =>
        (subscriptions?.find((subscriptionData) =>
            SubscriptionHelpers.isSubscriptionType(type, subscriptionData.subscription),
        ) ?? { subscription: getNoneSubscriptionForType(type), quantity: 0 }) as GenericSubscriptionData<T>,

    getEffectiveCompanySubscription: ({
        teamSubscriptions,
        developer,
    }: {
        teamSubscriptions: SubscriptionData[] | undefined;
        developer: boolean | undefined;
    }): CompanySubscription => {
        const companySubscription =
            SubscriptionHelpers.getSubscriptionData(SubscriptionType.COMPANY, teamSubscriptions)?.subscription ??
            CompanySubscription.NONE;

        if (developer) {
            return CompanySubscription.PREMIUM;
        }

        return companySubscription;
    },

    getSubscriptionTypeLabel: (subscription: Subscription) => {
        const { type } = SubscriptionHelpers.getSubscriptionAndType(subscription);

        return SubscriptionTypeLabelMap[type];
    },

    getSubscriptionChangeForType: <T extends SubscriptionType>(type: T, team: Team): ChangesForType<T> | undefined =>
        team.subscriptionUpdateNotice?.changes.find((change) =>
            SubscriptionHelpers.isSubscriptionType(type, change.subscription),
        ) as ChangesForType<T> | undefined,

    isGoingToBeCanceled: (
        subscriptionType: SubscriptionType,
        changes: NonNullable<Team['subscriptionUpdateNotice']>['changes'],
    ): boolean =>
        changes.some((change) => {
            if (!('isRemoved' in change)) {
                return false;
            }

            if (subscriptionType === SubscriptionType.CREDIT) {
                return (
                    SubscriptionHelpers.isSubscriptionType(SubscriptionType.CREDIT, change.subscription) ||
                    SubscriptionHelpers.isSubscriptionType(SubscriptionType.DESIGNER, change.subscription)
                );
            }

            return SubscriptionHelpers.isSubscriptionType(subscriptionType, change.subscription);
        }),

    getCancellationDate: (team: Team, type: SubscriptionType): string | null => {
        const change = SubscriptionHelpers.getSubscriptionChangeForType(type, team);
        return change && 'isRemoved' in change && team.subscriptionUpdateNotice?.date
            ? new Date(team.subscriptionUpdateNotice.date * 1000).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              })
            : null;
    },
};

const getNoneSubscriptionForType = <T extends SubscriptionType>(type: T): SubscriptionFromSubscriptionType<T> => {
    switch (type) {
        case SubscriptionType.COMPANY:
            return CompanySubscription.NONE as SubscriptionFromSubscriptionType<T>;
        case SubscriptionType.DESIGNER:
            return DesignerSubscription.NONE as SubscriptionFromSubscriptionType<T>;
        case SubscriptionType.CREDIT:
            return CreditSubscription.NONE as SubscriptionFromSubscriptionType<T>;
    }
};
